import { Rally, SetRecord, Team } from '@/types';
import { getSetScoreColor } from '@/lib/setScoreColor';

interface RallyHistoryModalProps {
  setNumber: number;
  setRecord: SetRecord;
  rallies: Rally[];
  teamA: Team;
  teamB: Team;
  onClose: () => void;
}

export default function RallyHistoryModal({
  setNumber,
  setRecord,
  rallies,
  teamA,
  teamB,
  onClose,
}: RallyHistoryModalProps) {
  // Normalize rally data to handle both snake_case (from DB) and camelCase (from app state)
  const normalizedRallies = rallies.map(r => ({
    id: r.id,
    setId: (r as any).setId || (r as any).set_id,
    rallyNumber: (r as any).rallyNumber || (r as any).rally_number,
    servingTeamId: (r as any).servingTeamId || (r as any).serving_team_id,
    serverId: (r as any).serverId || (r as any).server_id,
    pointWonById: (r as any).pointWonById || (r as any).point_won_by_id,
    lostReason: (r as any).lostReason || (r as any).lost_reason,
    pointWinnerId: (r as any).pointWinnerId || (r as any).point_winner_id,
    recordedAt: (r as any).recordedAt || (r as any).recorded_at,
  }));

  // Normalize setRecord to handle both snake_case and camelCase
  const normalizedSetRecord = {
    id: setRecord.id,
    matchId: (setRecord as any).matchId || (setRecord as any).match_id,
    setNumber: (setRecord as any).setNumber || (setRecord as any).set_number,
    scoreA: (setRecord as any).scoreA ?? (setRecord as any).score_a ?? 0,
    scoreB: (setRecord as any).scoreB ?? (setRecord as any).score_b ?? 0,
    winnerId: (setRecord as any).winnerId || (setRecord as any).winner_id,
    startedAt: (setRecord as any).startedAt || (setRecord as any).started_at,
    endedAt: (setRecord as any).endedAt || (setRecord as any).ended_at,
  };

  const allPlayers = [...teamA.players, ...teamB.players];
  const isCompleted = normalizedSetRecord.endedAt !== null;
  const winner = normalizedSetRecord.winnerId
    ? normalizedSetRecord.winnerId === teamA.id ? teamA : teamB
    : null;
  const { color: scoreColor } = getSetScoreColor(normalizedSetRecord, teamA, teamB);

  // Rallies are expected ascending (rally 1 first)
  const sortedRallies = [...normalizedRallies].sort((a, b) => a.rallyNumber - b.rallyNumber);

  // Compute running score after each rally
  const rallyScores: { a: number; b: number }[] = [];
  let runA = 0, runB = 0;
  for (const r of sortedRallies) {
    if (r.pointWonById === teamA.id) runA++;
    else if (r.pointWonById === teamB.id) runB++;
    rallyScores.push({ a: runA, b: runB });
  }

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="bg-surface border-b border-border px-4 py-4 safe-top flex items-center gap-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="p-2 -ml-2 rounded-lg text-text-muted hover:text-text-primary transition-colors"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 4l-6 6 6 6" />
          </svg>
        </button>
        <div className="flex-1">
          <h2 className="text-base font-bold text-text-primary">
            SET {setNumber} — RALLY HISTORY
          </h2>
          <p className="text-xs text-text-muted">
            {sortedRallies.length} {sortedRallies.length === 1 ? 'rally' : 'rallies'}
            {isCompleted && winner && winner.name ? ` · ${winner.name} won` : ''}
          </p>
        </div>
      </div>

      {/* Team Names Bar */}
      <div className="bg-surface border-b border-border px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-center gap-3">
          <span className="text-sm font-semibold" style={{ color: teamA.color }}>
            {teamA.name || teamA.shortName}
          </span>
          <span className="text-xs text-text-muted font-medium">vs</span>
          <span className="text-sm font-semibold" style={{ color: teamB.color }}>
            {teamB.name || teamB.shortName}
          </span>
        </div>
      </div>

      {/* Score Summary */}
      <div className="bg-surface border-b border-border px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-center gap-6">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase" style={{ color: teamA.color }}>
              {teamA.shortName || teamA.name}
            </p>
            <p className="text-3xl font-bold font-mono-nums" style={{ color: scoreColor ?? undefined }}>
              {String(normalizedSetRecord.scoreA).padStart(2, '0')}
            </p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-text-muted text-xs font-bold">vs</span>
            {winner && (
              <span
                className="text-xs font-bold px-2 py-0.5 rounded"
                style={{ backgroundColor: `${winner.color}30`, color: winner.color }}
              >
                {winner.shortName || winner.name} WON
              </span>
            )}
          </div>
          <div className="text-center">
            <p className="text-xs font-semibold uppercase" style={{ color: teamB.color }}>
              {teamB.shortName || teamB.name}
            </p>
            <p className="text-3xl font-bold font-mono-nums" style={{ color: scoreColor ?? undefined }}>
              {String(normalizedSetRecord.scoreB).padStart(2, '0')}
            </p>
          </div>
        </div>
      </div>

      {/* Column Headers */}
      <div className="bg-surface-raised border-b border-border px-3 py-2 flex-shrink-0">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase text-text-muted">
          <span className="w-7 text-center">#</span>
          <span className="w-8">SERV</span>
          <span className="flex-1">SERVER</span>
          <span className="w-3 text-center">→</span>
          <span className="w-8">WON</span>
          <span className="w-12">REASON</span>
          <span className="flex-1">WINNER</span>
          <span className="w-12 text-right">SCORE</span>
        </div>
      </div>

      {/* Rally List */}
      <div className="flex-1 overflow-y-auto">
        {sortedRallies.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-text-muted text-sm">No rallies recorded.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedRallies.map((rally, idx) => {
              const rallyServingTeam = rally.servingTeamId === teamA.id ? teamA : teamB;
              const rallyWinningTeam = rally.pointWonById === teamA.id ? teamA : teamB;
              const server = allPlayers.find(p => p.id === rally.serverId);
              const pointWinner = allPlayers.find(p => p.id === rally.pointWinnerId);
              const isServiceBreak = rally.pointWonById !== rally.servingTeamId;
              const score = rallyScores[idx];

              return (
                <div
                  key={rally.id}
                  className={`flex items-center gap-2 px-3 py-2.5 text-xs ${
                    isServiceBreak ? 'bg-error/5' : ''
                  }`}
                >
                  <span className="w-7 text-center text-text-muted font-mono-nums">
                    {rally.rallyNumber}
                  </span>
                  <span className="w-8 font-bold text-center" style={{ color: rallyServingTeam.color }}>
                    {rallyServingTeam.shortName || rallyServingTeam.name}
                  </span>
                  <span className="flex-1 truncate" style={{ color: rallyServingTeam.color }}>
                    {server?.name ?? '—'}
                  </span>
                  <span className="w-3 text-center text-text-muted">→</span>
                  <span className="w-8 font-bold text-center" style={{ color: rallyWinningTeam.color }}>
                    {rallyWinningTeam.shortName || rallyWinningTeam.name}
                  </span>
                  <span className="w-12 text-text-muted font-medium">
                    {rally.lostReason ?? '—'}
                  </span>
                  <span className="flex-1 truncate" style={{ color: rallyWinningTeam.color }}>
                    {pointWinner?.name ?? ''}
                  </span>
                  <span className="w-12 text-right font-mono font-bold text-[11px]">
                    <span style={{ color: teamA.color }}>{score.a}</span>
                    <span className="text-text-muted">–</span>
                    <span style={{ color: teamB.color }}>{score.b}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
